import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { 
  Box, 
  Typography, 
  IconButton, 
  Button, 
  Checkbox, 
  FormControlLabel,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Divider,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/system';
import RetroCloseButton from './RetroCloseButton';
import PaymentPopup from './PaymentPopup';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DOG_BREEDS } from '../data/dogBreeds';
import { fetchDogDescription, fetchDogImage, removeBlueBackground, ACTIONS } from '../utils/dogCardGenerator';

// Shop catalog shown in the console's embedded Shop view (mirrors /shop)
const SHOP_PRODUCTS = [
  {
    id: 1,
    name: 'Dog Cards Pack',
    description: 'Get all your saved dog cards printed as physical trading cards',
    price: '$50',
    features: ['Up to 25 cards', 'High-quality cardstock', 'Professional printing', 'US shipping included'],
    popular: false,
  },
  {
    id: 2,
    name: 'Custom Dog T-Shirts',
    description: 'Custom t-shirts featuring your favorite dog breeds with personalization',
    price: '$75',
    features: ['Multiple colors & sizes', 'Custom dog names', 'Breed descriptions', 'FREE dog card included'],
    popular: true,
  },
  {
    id: 3,
    name: 'Custom Dog Hats',
    description: 'Stylish baseball caps with your favorite dog breed designs',
    price: '$75',
    features: ['Adjustable baseball caps', 'Custom dog names', 'Multiple color options', 'FREE dog card included'],
    popular: false,
  },
];

// Existing styled components from your Dashboard
const PopupContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '400px',
  // Include padding in the width so the card never renders wider than the
  // viewport on mobile.
  boxSizing: 'border-box',
  padding: '20px',
  // Matches the subscribe popup exactly: bordered rounded card floating
  // over the dimmed page with its full outline visible
  background: 'linear-gradient(180deg, #1a237e 0%, #283593 100%)',
  border: '2px solid #00BFFF',
  borderRadius: '16px',
  textAlign: 'center',
  zIndex: 10000,
  boxShadow: '0px 0px 20px 0px rgba(0, 191, 255, 0.5)',
  // Fixed console height: the card stays the same size whichever view
  // (dashboard, subscribe, merch) is showing inside it
  height: 'min(640px, calc(100vh - 90px))',
  overflowY: 'auto',
  '@media (max-width: 600px)': {
    padding: '14px',
  },
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(8, 11, 19, 0.85)',
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const RetroTitle = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontSize: '1.2rem',
  textShadow: `0 0 10px #FFFFFF, 0 0 20px #00BFFF, 0 0 30px #00BFFF`,
  fontFamily: "'Press Start 2P', cursive",
  marginBottom: '12px',
  padding: '0 40px',
  '@media (max-width: 600px)': {
    fontSize: '0.85rem',
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 191, 255, 0.15)',
  padding: '10px',
  borderRadius: '10px',
  margin: '8px 0',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));

const StatValue = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 'bold',
  color: '#50E3C2',
  textShadow: '0 0 10px rgba(80, 227, 194, 0.5)',
  fontFamily: "'Press Start 2P', cursive",
  margin: '4px 0',
});

const StatLabel = styled(Typography)({
  fontSize: '0.68rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: '#FFFFFF',
  fontFamily: "'Press Start 2P', cursive",
});

const DateTimeDisplay = styled(Typography)({
  fontSize: '0.65rem',
  color: '#FFFFFF',
  opacity: 0.7,
  marginTop: '10px',
  fontFamily: "'Press Start 2P', cursive",
});

// New styled components for PaymentPopup
const SummaryText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontSize: '1rem',
  marginBottom: '15px',
  '@media (max-width: 600px)': {
    fontSize: '0.9rem',
  },
}));

const CopyField = styled(TextField)(({ theme }) => ({
  backgroundColor: '#283593',
  borderRadius: '5px',
  marginBottom: '15px',
  input: {
    color: '#FFFFFF',
    fontFamily: "'Press Start 2P', cursive",
    fontSize: '0.9rem',
    '@media (max-width: 600px)': {
      fontSize: '0.8rem',
    },
  },
}));

const FlexContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginTop: '-10px',
  marginBottom: '10px',
}));

const LabelText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontSize: '0.9rem',
  marginTop: '-10px',
  marginBottom: '10px',
  '@media (max-width: 600px)': {
    fontSize: '0.8rem',
  },
}));

// Card Pack Option Component
const CardPackOption = ({ price, cardCount, selectedPack, onSelect }) => {
  const isSelected = selectedPack === price;

  return (
    <Box
      onClick={() => onSelect(price, cardCount)}
      sx={{
        backgroundColor: isSelected
          ? 'rgba(0, 191, 255, 0.3)'
          : 'rgba(0, 191, 255, 0.1)',
        padding: '15px',
        borderRadius: '8px',
        cursor: 'pointer',
        border: isSelected
          ? '2px solid #00BFFF'
          : '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(0, 191, 255, 0.2)',
          transform: 'translateY(-2px)',
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30%',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{
          color: '#FFFFFF',
          fontFamily: "'Press Start 2P', cursive",
          fontSize: '1.2rem',
          mb: 1,
        }}
      >
        ${price}
      </Typography>
      <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>
        {cardCount} Cards
      </Typography>
    </Box>
  );
};

// Dog Products Customization Component - New Theme: Dog T's, Hats n' Cards
const DogProductsCustomizer = ({ selectedCard, savedCards, onClose }) => {
  const [productSelection, setProductSelection] = useState('individual'); // 'individual', 'combo2', 'combo3'
  const [selectedProducts, setSelectedProducts] = useState(['t-shirt']); // Can include 't-shirt', 'hat', 'socks', 'cards'
  const [color, setColor] = useState('white');
  const [size, setSize] = useState('M');
  const [dogName, setDogName] = useState('');
  const [namePosition, setNamePosition] = useState('above');
  const [includeDescription, setIncludeDescription] = useState(true);
  const [descriptionPosition, setDescriptionPosition] = useState('below');
  const [includeBreedName, setIncludeBreedName] = useState(true);
  const [breedPosition, setBreedPosition] = useState('below');
  const [includeQRCode, setIncludeQRCode] = useState(false);
  const [qrCodePosition, setQRCodePosition] = useState('bottom-right');
  
  // Static test QR code data - can be customized later
  const testQRCodeData = 'https://satoshiadvance.com/dog-cards-test';

  const colors = [
    { value: 'white', label: 'White' },
    { value: 'black', label: 'Black' },
    { value: 'navy', label: 'Navy Blue' },
    { value: 'red', label: 'Red' },
    { value: 'pink', label: 'Pink' },
    { value: 'lightblue', label: 'Light Blue' },
    { value: 'gray', label: 'Heather Gray' }
  ];

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  // Printed text (custom dog name / breed name) shown on the garment
  // previews above and below the dog sprite, per the position options
  const isLightGarment =
    color === 'white' || color === 'gray' || color === 'pink' || color === 'lightblue';
  const printTextColor = isLightGarment ? '#1a1a1a' : '#FFFFFF';
  const printTextShadow = isLightGarment ? 'none' : '1px 1px 2px rgba(0,0,0,0.6)';
  const topPrintLines = [
    ...(dogName.trim() && namePosition === 'above' ? [dogName.trim()] : []),
    ...(includeBreedName && selectedCard?.breedName && breedPosition === 'above'
      ? [selectedCard.breedName]
      : [])
  ];
  const bottomPrintLines = [
    ...(dogName.trim() && namePosition === 'below' ? [dogName.trim()] : []),
    ...(includeBreedName && selectedCard?.breedName && breedPosition === 'below'
      ? [selectedCard.breedName]
      : [])
  ];
  const renderPrintLines = (lines, sxOverrides) =>
    lines.length > 0 && (
      <Box sx={{ position: 'absolute', textAlign: 'center', zIndex: 2, ...sxOverrides }}>
        {lines.map((line, i) => (
          <Typography
            key={i}
            sx={{
              color: printTextColor,
              textShadow: printTextShadow,
              fontWeight: 'bold',
              fontSize: 'inherit',
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    );

  // Product pricing
  const getProductPrice = () => {
    switch(productSelection) {
      case 'combo3': return 125; // All 3: Cards + Hat + Tee
      case 'combo2': 
        if (selectedProducts.includes('cards')) return 100; // Cards + (Hat or Tee)
        return 125; // Hat + Tee + complementary card
      case 'individual':
        if (selectedProducts.includes('cards')) return 50; // Card pack only
        if (selectedProducts.includes('socks')) return 50; // Socks + complementary card
        return 75; // Single hat or tee + complementary card
      default: return 75;
    }
  };

  const getProductDescription = () => {
    switch(productSelection) {
      case 'combo3': 
        return 'Complete Dog Bundle: Card Pack (25 cards) + Custom Hat + Custom T-Shirt';
      case 'combo2':
        if (selectedProducts.includes('cards') && selectedProducts.includes('t-shirt')) {
          return 'Card Pack (25 cards) + Custom T-Shirt';
        } else if (selectedProducts.includes('cards') && selectedProducts.includes('hat')) {
          return 'Card Pack (25 cards) + Custom Hat';
        } else {
          return 'Custom Hat + Custom T-Shirt + Complementary Dog Card';
        }
      case 'individual':
        if (selectedProducts.includes('cards')) {
          return `Card Pack: All your saved dog cards (up to 25) printed as physical cards`;
        } else if (selectedProducts.includes('t-shirt')) {
          return `Custom T-Shirt + Complementary ${selectedCard?.breedName} Dog Card`;
        } else if (selectedProducts.includes('socks')) {
          return `Custom Socks + Complementary ${selectedCard?.breedName} Dog Card`;
        } else {
          return `Custom Hat + Complementary ${selectedCard?.breedName} Dog Card`;
        }
      default: return 'Custom Product';
    }
  };

  const handleProductSelectionChange = (selection) => {
    setProductSelection(selection);
    switch(selection) {
      case 'combo3':
        setSelectedProducts(['t-shirt', 'hat', 'cards']);
        break;
      case 'combo2':
        setSelectedProducts(['t-shirt', 'cards']); // Default combo2
        break;
      case 'individual':
        // If no specific card selected, default to cards, otherwise t-shirt
        setSelectedProducts([selectedCard ? 't-shirt' : 'cards']);
        break;
    }
  };

  const handleIndividualProductChange = (product) => {
    setSelectedProducts([product]);
  };

  const handleCombo2Change = (products) => {
    setSelectedProducts(products);
  };

  const handleProceedToPayment = () => {
    // Validation checks
    if (selectedProducts.includes('cards') && (!savedCards || savedCards.length === 0)) {
      alert('❌ No saved cards found. Please generate and save some dog cards first!');
      return;
    }

    if ((selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks')) && !selectedCard) {
      alert('❌ Please select a specific dog card to customize hats and t-shirts!');
      return;
    }

    const customization = {
      productSelection,
      selectedProducts,
      color: selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks') ? color : null,
      size: selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks') ? size : null,
      dogName,
      namePosition,
      includeDescription,
      descriptionPosition,
      includeBreedName,
      breedPosition,
      includeQRCode,
      qrCodePosition,
      qrCodeData: testQRCodeData,
      cardData: selectedCard,
      savedCards: selectedProducts.includes('cards') ? savedCards : null,
      price: getProductPrice()
    };
    
    // Proceed to payment
    onClose(customization);
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ position: 'relative' }}>
        <RetroCloseButton onClick={onClose} label="Close" sx={{ top: 0, right: 0 }} />

        <RetroTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          '@media (max-width: 600px)': {
            fontSize: '1.2rem',
          },
        }}>
          <LocalShippingIcon />
          Dog T's, Hats n' Cards
        </RetroTitle>

        <Typography
          sx={{
            color: '#FFFFFF',
            fontSize: '0.9rem',
            textAlign: 'center',
            mb: 2,
            '@media (max-width: 600px)': {
              fontSize: '0.8rem',
              mb: 1,
            },
          }}
        >
          {selectedCard ? 
            `Create custom products with your ${selectedCard.breedName}! Choose individual items or save with bundles.` :
            'Create custom dog products! Choose individual items or save with bundles.'
          }
        </Typography>

        {/* Product Selection Options */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ 
            color: '#FFFFFF', 
            fontSize: '1rem', 
            mb: 2,
            fontFamily: "'Press Start 2P', cursive",
            textAlign: 'center'
          }}>
            Choose Your Products:
          </Typography>
          
          {/* Product Bundle Options */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Complete Bundle */}
            <Box
              onClick={selectedCard ? () => handleProductSelectionChange('combo3') : undefined}
              sx={{
                backgroundColor: productSelection === 'combo3' ? 'rgba(0, 191, 255, 0.3)' : 
                                selectedCard ? 'rgba(0, 191, 255, 0.1)' : 'rgba(128, 128, 128, 0.1)',
                padding: '15px',
                borderRadius: '8px',
                cursor: selectedCard ? 'pointer' : 'not-allowed',
                border: productSelection === 'combo3' ? '2px solid #00BFFF' : 
                        selectedCard ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(128, 128, 128, 0.2)',
                transition: 'all 0.2s ease',
                opacity: selectedCard ? 1 : 0.5,
                '&:hover': { backgroundColor: selectedCard ? 'rgba(0, 191, 255, 0.2)' : undefined },
              }}
            >
              <Typography sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 1 }}>
                🎉 Complete Dog Bundle - $125 {!selectedCard && '(Requires specific card)'}
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>
                Card Pack ({savedCards?.length || 0} cards) + Custom Hat + Custom T-Shirt
              </Typography>
              <Typography sx={{ color: '#50E3C2', fontSize: '0.7rem', mt: 1 }}>
                💰 Save $50! (Individual price: $175)
              </Typography>
            </Box>

            {/* 2-Product Combo */}
            <Box
              onClick={selectedCard ? () => handleProductSelectionChange('combo2') : undefined}
              sx={{
                backgroundColor: productSelection === 'combo2' ? 'rgba(0, 191, 255, 0.3)' : 
                                selectedCard ? 'rgba(0, 191, 255, 0.1)' : 'rgba(128, 128, 128, 0.1)',
                padding: '15px',
                borderRadius: '8px',
                cursor: selectedCard ? 'pointer' : 'not-allowed',
                border: productSelection === 'combo2' ? '2px solid #00BFFF' : 
                        selectedCard ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(128, 128, 128, 0.2)',
                transition: 'all 0.2s ease',
                opacity: selectedCard ? 1 : 0.5,
                '&:hover': { backgroundColor: selectedCard ? 'rgba(0, 191, 255, 0.2)' : undefined },
              }}
            >
              <Typography sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 1 }}>
                🎯 Pick Any 2 - $100-125 {!selectedCard && '(Requires specific card)'}
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>
                Choose any 2 products (Cards + Hat/Tee: $100, Hat + Tee: $125)
              </Typography>
              <Typography sx={{ color: '#50E3C2', fontSize: '0.7rem', mt: 1 }}>
                💰 Save $25-50!
              </Typography>
            </Box>

            {/* Individual Product */}
            <Box
              onClick={() => handleProductSelectionChange('individual')}
              sx={{
                backgroundColor: productSelection === 'individual' ? 'rgba(0, 191, 255, 0.3)' : 'rgba(0, 191, 255, 0.1)',
                padding: '15px',
                borderRadius: '8px',
                cursor: 'pointer',
                border: productSelection === 'individual' ? '2px solid #00BFFF' : '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
                '&:hover': { backgroundColor: 'rgba(0, 191, 255, 0.2)' },
              }}
            >
              <Typography sx={{ color: '#FFFFFF', fontWeight: 'bold', mb: 1 }}>
                🎨 Individual Item - $50-75
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>
                Card Pack: $50 | Hat or Tee (+ free dog card): $75
              </Typography>
            </Box>
          </Box>

          {/* Specific Product Selection based on choice */}
          {productSelection === 'combo2' && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.9rem', mb: 1 }}>
                Select your 2 products:
              </Typography>
              <RadioGroup
                value={selectedProducts.join(',')}
                onChange={(e) => handleCombo2Change(e.target.value.split(','))}
                sx={{ ml: 2 }}
              >
                <FormControlLabel 
                  value="cards,t-shirt" 
                  control={<Radio sx={{ color: '#00BFFF' }} />} 
                  label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Card Pack + Custom T-Shirt ($100)</Typography>}
                  disabled={!selectedCard}
                />
                <FormControlLabel 
                  value="cards,hat" 
                  control={<Radio sx={{ color: '#00BFFF' }} />} 
                  label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Card Pack + Custom Hat ($100)</Typography>}
                  disabled={!selectedCard}
                />
                <FormControlLabel 
                  value="t-shirt,hat" 
                  control={<Radio sx={{ color: '#00BFFF' }} />} 
                  label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Custom Hat + T-Shirt + Free {selectedCard?.breedName || 'Dog'} Card ($125)</Typography>}
                  disabled={!selectedCard}
                />
              </RadioGroup>
              {!selectedCard && (
                <Typography sx={{ color: '#FF9800', fontSize: '0.7rem', mt: 1, ml: 2 }}>
                  ⚠️ Select a specific dog card first to customize hats and t-shirts
                </Typography>
              )}
            </Box>
          )}

          {productSelection === 'individual' && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.9rem', mb: 1 }}>
                Choose your product:
              </Typography>
              <RadioGroup
                value={selectedProducts[0]}
                onChange={(e) => handleIndividualProductChange(e.target.value)}
                sx={{ ml: 2 }}
              >
                <FormControlLabel 
                  value="cards" 
                  control={<Radio sx={{ color: '#00BFFF' }} />} 
                  label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Card Pack - All your saved cards ({savedCards?.length || 0} cards) ($50)</Typography>}
                />
                {selectedCard && (
                  <>
                    <FormControlLabel 
                      value="t-shirt" 
                      control={<Radio sx={{ color: '#00BFFF' }} />} 
                      label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Custom T-Shirt + Free {selectedCard.breedName} Card ($75)</Typography>}
                    />
                    <FormControlLabel 
                      value="hat" 
                      control={<Radio sx={{ color: '#00BFFF' }} />} 
                      label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Custom Hat + Free {selectedCard.breedName} Card ($75)</Typography>}
                    />
                    <FormControlLabel 
                      value="socks" 
                      control={<Radio sx={{ color: '#00BFFF' }} />} 
                      label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Custom Socks + Free {selectedCard.breedName} Card ($50)</Typography>}
                    />
                  </>
                )}
              </RadioGroup>
            </Box>
          )}
        </Box>

        {/* Enhanced Product Preview */}
        {(selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks')) && (
        <Box
          sx={{
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '8px',
            padding: '15px',
            mb: 3,
            textAlign: 'center',
            '@media (max-width: 600px)': {
              padding: '10px',
              mb: 2,
            },
          }}
        >
          <Typography sx={{ 
            color: '#FFFFFF', 
            fontSize: '0.9rem', 
            mb: 2,
            fontFamily: "'Press Start 2P', cursive",
            '@media (max-width: 600px)': {
              fontSize: '0.8rem',
              mb: 1,
            },
          }}>
            Preview: {getProductDescription()}
          </Typography>
          
          {/* Visual Product Preview — zoom scales every mockup (and their
              internal details) up together: ~1.9x on desktop, smaller on
              phones so the row still fits the popup width */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 2, zoom: { xs: 1.35, sm: 1.9 } }}>
            {/* T-Shirt Preview: real tee silhouette in the chosen color,
                printing the isolated dog sprite (no card frame) */}
            {selectedProducts.includes('t-shirt') && (
              <Box
                sx={{
                  position: 'relative',
                  width: '150px',
                  height: '150px',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))'
                }}
              >
                {/* Outline layer (slightly larger silhouette behind) */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: '-2px',
                    backgroundColor: 'rgba(0, 0, 0, 0.45)',
                    clipPath:
                      'polygon(32% 0%, 42% 7%, 58% 7%, 68% 0%, 100% 14%, 92% 34%, 80% 28%, 80% 100%, 20% 100%, 20% 28%, 8% 34%, 0% 14%)'
                  }}
                />
                {/* Shirt body in the selected color */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor:
                      color === 'white' ? '#F5F5F5' :
                      color === 'black' ? '#1a1a1a' :
                      color === 'navy' ? '#1a237e' :
                      color === 'red' ? '#d32f2f' :
                      color === 'pink' ? '#f48fb1' :
                      color === 'lightblue' ? '#7ec8f0' : '#9e9e9e',
                    clipPath:
                      'polygon(32% 0%, 42% 7%, 58% 7%, 68% 0%, 100% 14%, 92% 34%, 80% 28%, 80% 100%, 20% 100%, 20% 28%, 8% 34%, 0% 14%)'
                  }}
                />
                {/* Collar ribbing */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '3px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '26%',
                    height: '9px',
                    borderRadius: '0 0 50% 50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.25)'
                  }}
                />

                {/* Isolated dog print centered on the chest */}
                {selectedCard && (
                  <Box
                    component="img"
                    src={selectedCard.spriteUrl || selectedCard.imageUrl}
                    alt={selectedCard.breedName}
                    sx={{
                      position: 'absolute',
                      top: '52%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '72px',
                      height: '72px',
                      objectFit: 'contain',
                      // Only add the card-style frame when falling back to the
                      // full card image (older saves without a sprite)
                      ...(selectedCard.spriteUrl
                        ? {}
                        : {
                            borderRadius: '4px',
                            border: '2px solid #FFFFFF',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }),
                      zIndex: 2
                    }}
                  />
                )}

                {/* Printed dog name / breed name above and below the sprite */}
                {renderPrintLines(topPrintLines, { top: '16px', left: '25%', width: '50%', fontSize: '0.42rem' })}
                {renderPrintLines(bottomPrintLines, { bottom: '22px', left: '25%', width: '50%', fontSize: '0.42rem' })}

                {/* Size label */}
                <Typography 
                  sx={{ 
                    color: '#FFFFFF', 
                    fontSize: '0.6rem', 
                    fontWeight: 'bold',
                    position: 'absolute',
                    bottom: '8px',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  Size: {size}
                </Typography>
                
                {/* QR Code Preview */}
                {includeQRCode && (
                  <Box
                    sx={{
                      position: 'absolute',
                      [qrCodePosition.includes('bottom') ? 'bottom' : 'top']: '4px',
                      [qrCodePosition.includes('right') ? 'right' : 'left']: '4px',
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '6px',
                      color: '#000',
                      fontWeight: 'bold',
                      borderRadius: '1px'
                    }}
                  >
                    QR
                  </Box>
                )}
              </Box>
            )}

            {/* Hat Preview: baseball-cap silhouette styled like the tee —
                dome + brim in the chosen color with the dog printed on the
                front panel */}
            {selectedProducts.includes('hat') && (
              <Box
                sx={{
                  position: 'relative',
                  width: '130px',
                  height: '110px',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))'
                }}
              >
                {/* Crown outline */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '6px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '104px',
                    height: '74px',
                    borderRadius: '52px 52px 10px 10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.45)'
                  }}
                />
                {/* Crown in the selected color */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100px',
                    height: '72px',
                    borderRadius: '50px 50px 9px 9px',
                    backgroundColor:
                      color === 'white' ? '#F5F5F5' :
                      color === 'black' ? '#1a1a1a' :
                      color === 'navy' ? '#1a237e' :
                      color === 'red' ? '#d32f2f' :
                      color === 'pink' ? '#f48fb1' :
                      color === 'lightblue' ? '#7ec8f0' : '#9e9e9e'
                  }}
                />
                {/* Panel seams */}
                <Box sx={{ position: 'absolute', top: '12px', left: 'calc(50% - 17px)', width: '2px', height: '64px', backgroundColor: 'rgba(0,0,0,0.18)', borderRadius: '2px' }} />
                <Box sx={{ position: 'absolute', top: '12px', left: 'calc(50% + 15px)', width: '2px', height: '64px', backgroundColor: 'rgba(0,0,0,0.18)', borderRadius: '2px' }} />
                {/* Top button */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.35)'
                  }}
                />
                {/* Brim (curved, slightly darker shade of the color) */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '76px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '124px',
                    height: '24px',
                    borderRadius: '8px 8px 62px 62px',
                    border: '2px solid rgba(0, 0, 0, 0.45)',
                    boxSizing: 'border-box',
                    backgroundColor:
                      color === 'white' ? '#E3E3E3' :
                      color === 'black' ? '#000000' :
                      color === 'navy' ? '#141b63' :
                      color === 'red' ? '#b52828' :
                      color === 'pink' ? '#ec6a99' :
                      color === 'lightblue' ? '#5bb0e0' : '#8a8a8a'
                  }}
                />

                {/* Isolated dog print on the front panel */}
                {selectedCard && (
                  <Box
                    component="img"
                    src={selectedCard.spriteUrl || selectedCard.imageUrl}
                    alt={selectedCard.breedName}
                    sx={{
                      position: 'absolute',
                      top: '26px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '44px',
                      height: '44px',
                      objectFit: 'contain',
                      ...(selectedCard.spriteUrl ? {} : { borderRadius: '3px', border: '1px solid #FFFFFF' }),
                      zIndex: 2
                    }}
                  />
                )}

                {/* Printed dog name / breed name: crown above the sprite, brim below */}
                {renderPrintLines(topPrintLines, { top: '11px', left: '50%', transform: 'translateX(-50%)', width: '76px', fontSize: '0.36rem' })}
                {renderPrintLines(bottomPrintLines, { top: '79px', left: '50%', transform: 'translateX(-50%)', width: '108px', fontSize: '0.36rem' })}

                {/* QR Code Preview */}
                {includeQRCode && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '14px',
                      right: '14px',
                      width: '10px',
                      height: '10px',
                      backgroundColor: color === 'white' || color === 'gray' || color === 'pink' || color === 'lightblue' ? '#000' : '#FFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '4px',
                      color: color === 'white' || color === 'gray' || color === 'pink' || color === 'lightblue' ? '#FFF' : '#000',
                      zIndex: 3
                    }}
                  >
                    QR
                  </Box>
                )}
              </Box>
            )}

            {/* Socks Preview: side-profile sock styled like the tee/cap —
                cuff, leg, and curved foot in the chosen color with the dog
                printed on the ankle */}
            {selectedProducts.includes('socks') && (
              <Box
                sx={{
                  position: 'relative',
                  width: '110px',
                  height: '140px',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))'
                }}
              >
                {/* Leg */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '4px',
                    right: '14px',
                    width: '50px',
                    height: '92px',
                    borderRadius: '10px 10px 0 0',
                    border: '2px solid rgba(0, 0, 0, 0.45)',
                    borderBottom: 'none',
                    boxSizing: 'border-box',
                    backgroundColor:
                      color === 'white' ? '#F5F5F5' :
                      color === 'black' ? '#1a1a1a' :
                      color === 'navy' ? '#1a237e' :
                      color === 'red' ? '#d32f2f' :
                      color === 'pink' ? '#f48fb1' :
                      color === 'lightblue' ? '#7ec8f0' : '#9e9e9e'
                  }}
                />
                {/* Cuff ribbing */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '4px',
                    right: '14px',
                    width: '50px',
                    height: '16px',
                    borderRadius: '10px 10px 0 0',
                    border: '2px solid rgba(0, 0, 0, 0.45)',
                    boxSizing: 'border-box',
                    backgroundColor: 'rgba(0, 0, 0, 0.22)',
                    backgroundImage:
                      'repeating-linear-gradient(90deg, transparent 0 4px, rgba(0,0,0,0.18) 4px 6px)'
                  }}
                />
                {/* Foot (curved toe pointing left) */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '4px',
                    width: '92px',
                    height: '44px',
                    borderRadius: '26px 0 14px 26px',
                    border: '2px solid rgba(0, 0, 0, 0.45)',
                    boxSizing: 'border-box',
                    backgroundColor:
                      color === 'white' ? '#F5F5F5' :
                      color === 'black' ? '#1a1a1a' :
                      color === 'navy' ? '#1a237e' :
                      color === 'red' ? '#d32f2f' :
                      color === 'pink' ? '#f48fb1' :
                      color === 'lightblue' ? '#7ec8f0' : '#9e9e9e'
                  }}
                />
                {/* Heel + toe shading */}
                <Box sx={{ position: 'absolute', bottom: '12px', right: '18px', width: '26px', height: '22px', borderRadius: '0 0 12px 4px', backgroundColor: 'rgba(0,0,0,0.2)' }} />
                <Box sx={{ position: 'absolute', bottom: '14px', left: '8px', width: '22px', height: '32px', borderRadius: '18px 4px 4px 18px', backgroundColor: 'rgba(0,0,0,0.2)' }} />

                {/* Isolated dog print on the ankle */}
                {selectedCard && (
                  <Box
                    component="img"
                    src={selectedCard.spriteUrl || selectedCard.imageUrl}
                    alt={selectedCard.breedName}
                    sx={{
                      position: 'absolute',
                      top: '38px',
                      right: '20px',
                      width: '38px',
                      height: '38px',
                      objectFit: 'contain',
                      ...(selectedCard.spriteUrl ? {} : { borderRadius: '3px', border: '1px solid #FFFFFF' }),
                      zIndex: 2
                    }}
                  />
                )}

                {/* Printed dog name / breed name: leg above the sprite, foot below */}
                {renderPrintLines(topPrintLines, { top: '24px', right: '16px', width: '46px', fontSize: '0.34rem' })}
                {renderPrintLines(bottomPrintLines, { bottom: '18px', left: '6px', width: '88px', fontSize: '0.34rem' })}
              </Box>
            )}

            {/* Cards Preview */}
            {selectedProducts.includes('cards') && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  width: '60px',
                  height: '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #ccc',
                  padding: '4px',
                }}
              >
                <Typography sx={{ color: '#000', fontSize: '0.5rem', mb: 0.5 }}>
                  CARDS
                </Typography>
                <Typography sx={{ color: '#000', fontSize: '0.6rem', fontWeight: 'bold' }}>
                  {savedCards?.length || 0}
                </Typography>
                <Typography sx={{ color: '#000', fontSize: '0.4rem' }}>
                  cards
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        )}

        {/* Customization Options - Only show if hat or t-shirt selected */}
        {(selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks')) && (
        <>
          {/* Color Selection */}
          <FormControl fullWidth sx={{ 
            mb: 2,
            '@media (max-width: 600px)': {
              mb: 1.5,
            },
          }}>
            <InputLabel sx={{ 
              color: '#FFFFFF',
              '@media (max-width: 600px)': {
                fontSize: '0.9rem',
              },
            }}>Color</InputLabel>
            <Select
              value={color}
              onChange={(e) => setColor(e.target.value)}
              MenuProps={{ sx: { zIndex: 10001 } }}
              sx={{
                backgroundColor: '#283593',
                color: '#FFFFFF',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
                '@media (max-width: 600px)': {
                  fontSize: '0.9rem',
                },
              }}
            >
              {colors.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Size Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#FFFFFF' }}>Size</InputLabel>
            <Select
              value={size}
              onChange={(e) => setSize(e.target.value)}
              MenuProps={{ sx: { zIndex: 10001 } }}
              sx={{
                backgroundColor: '#283593',
                color: '#FFFFFF',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
              }}
            >
              {sizes.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
        )}

        {/* Custom Options - Only show if individual hat/tee selected */}
        {(selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks')) && !selectedProducts.includes('cards') && selectedProducts.length === 1 && (
        <>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
          
          <Typography sx={{ 
            color: '#FFFFFF', 
            fontSize: '1rem', 
            mb: 2,
            fontFamily: "'Press Start 2P', cursive",
            textAlign: 'center'
          }}>
            Customize Your {selectedProducts[0] === 't-shirt' ? 'T-Shirt' : selectedProducts[0] === 'socks' ? 'Socks' : 'Hat'}:
          </Typography>

          {/* Dog Name Input */}
          <TextField
            label="Dog Name (Optional)"
            value={dogName}
            onChange={(e) => setDogName(e.target.value)}
            fullWidth
            sx={{
              mb: 2,
              backgroundColor: '#283593',
              borderRadius: '5px',
              input: { color: '#FFFFFF' },
              label: { color: '#FFFFFF' },
              '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
            }}
          />

        {/* Name Position (only if dog name is provided) */}
        {dogName && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              Name Position:
            </Typography>
            <RadioGroup
              value={namePosition}
              onChange={(e) => setNamePosition(e.target.value)}
              row
            >
              <FormControlLabel 
                value="above" 
                control={<Radio sx={{ color: '#00BFFF' }} />}
                label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem' }}>Top</Typography>}
              />
              <FormControlLabel 
                value="below" 
                control={<Radio sx={{ color: '#00BFFF' }} />}
                label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem' }}>Bottom</Typography>}
              />
            </RadioGroup>
          </Box>
        )}

        {/* Include Description */}
        <FormControlLabel
          control={
            <Checkbox
              checked={includeDescription}
              onChange={(e) => setIncludeDescription(e.target.checked)}
              sx={{ color: '#00BFFF' }}
            />
          }
          label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Include Description</Typography>}
          sx={{ mb: 1 }}
        />

        {/* Description Position (only if description is included) */}
        {includeDescription && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: '#FFFFFF' }}>Description Position</InputLabel>
            <Select
              value={descriptionPosition}
              onChange={(e) => setDescriptionPosition(e.target.value)}
              MenuProps={{ sx: { zIndex: 10001 } }}
              sx={{
                backgroundColor: '#283593',
                color: '#FFFFFF',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
              }}
            >
              <MenuItem value="above">Above Card</MenuItem>
              <MenuItem value="below">Below Card</MenuItem>
              <MenuItem value="left">Left (Landscape)</MenuItem>
              <MenuItem value="right">Right (Landscape)</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Include Breed Name */}
        <FormControlLabel
          control={
            <Checkbox
              checked={includeBreedName}
              onChange={(e) => setIncludeBreedName(e.target.checked)}
              sx={{ color: '#00BFFF' }}
            />
          }
          label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Include Breed Name</Typography>}
          sx={{ mb: 1 }}
        />

        {/* Breed Position (only if breed name is included) */}
        {includeBreedName && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              Breed Name Position:
            </Typography>
            <RadioGroup
              value={breedPosition}
              onChange={(e) => setBreedPosition(e.target.value)}
              row
            >
              <FormControlLabel 
                value="above" 
                control={<Radio sx={{ color: '#00BFFF' }} />}
                label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem' }}>Top</Typography>}
              />
              <FormControlLabel 
                value="below" 
                control={<Radio sx={{ color: '#00BFFF' }} />}
                label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem' }}>Bottom</Typography>}
              />
            </RadioGroup>
          </Box>
        )}

        {/* Include QR Code */}
        <FormControlLabel
          control={
            <Checkbox
              checked={includeQRCode}
              onChange={(e) => setIncludeQRCode(e.target.checked)}
              sx={{ color: '#00BFFF' }}
            />
          }
          label={<Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem' }}>Include QR Code (Test Link)</Typography>}
          sx={{ mb: 1 }}
        />

        {/* QR Code Position (only if QR code is included) */}
        {includeQRCode && (
          <Box sx={{ mb: 2, ml: 3 }}>
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              QR Code Position:
            </Typography>
            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel sx={{ color: '#FFFFFF' }}>Position</InputLabel>
              <Select
                value={qrCodePosition}
                onChange={(e) => setQRCodePosition(e.target.value)}
                MenuProps={{ sx: { zIndex: 10001 } }}
                sx={{
                  backgroundColor: '#283593',
                  color: '#FFFFFF',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
                }}
              >
                <MenuItem value="bottom-right">Bottom Right</MenuItem>
                <MenuItem value="bottom-left">Bottom Left</MenuItem>
                <MenuItem value="top-right">Top Right</MenuItem>
                <MenuItem value="top-left">Top Left</MenuItem>
              </Select>
            </FormControl>
            <Typography sx={{ color: '#50E3C2', fontSize: '0.7rem' }}>
              📱 Test QR Code links to: {testQRCodeData}
            </Typography>
          </Box>
        )}

        {/* QR Code Preview Section */}
        {includeQRCode && (selectedProducts.includes('t-shirt') || selectedProducts.includes('hat') || selectedProducts.includes('socks')) && (
          <Box
            sx={{
              backgroundColor: 'rgba(0, 191, 255, 0.1)',
              borderRadius: '8px',
              padding: '15px',
              mb: 3,
              textAlign: 'center',
              border: '1px solid #00BFFF'
            }}
          >
            <Typography sx={{ 
              color: '#FFFFFF', 
              fontSize: '0.9rem', 
              mb: 2,
              fontFamily: "'Press Start 2P', cursive"
            }}>
              📱 QR Code Preview
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <QRCodeCanvas
                value={testQRCodeData}
                size={80}
                level="M"
                style={{ 
                  backgroundColor: 'white',
                  padding: '8px',
                  borderRadius: '4px'
                }}
              />
            </Box>
            
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem', mb: 1 }}>
              Position: {qrCodePosition.replace('-', ' ').toUpperCase()}
            </Typography>
            <Typography sx={{ color: '#50E3C2', fontSize: '0.6rem' }}>
              🔗 Links to: {testQRCodeData}
            </Typography>
            <Typography sx={{ color: '#FF9800', fontSize: '0.6rem', mt: 1 }}>
              ⚠️ Test QR Code - Can be customized later
            </Typography>
          </Box>
        )}
        </>
        )}

        {/* Order Summary */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '8px',
            padding: '15px',
            mb: 3,
            border: '1px solid #00BFFF'
          }}
        >
          <Typography sx={{ 
            color: '#FFFFFF', 
            fontSize: '1rem', 
            mb: 1,
            fontFamily: "'Press Start 2P', cursive",
            textAlign: 'center'
          }}>
            Order Summary
          </Typography>
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', textAlign: 'center', mb: 2 }}>
            {getProductDescription()}
          </Typography>
          <Typography sx={{ 
            color: '#50E3C2', 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            textAlign: 'center',
            fontFamily: "'Press Start 2P', cursive"
          }}>
            ${getProductPrice()}.00
          </Typography>
          {selectedProducts.includes('cards') && (
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem', textAlign: 'center', mt: 1 }}>
              📦 {savedCards?.length || 0} cards will be printed and shipped
            </Typography>
          )}
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem', textAlign: 'center', mt: 1 }}>
            ⏰ Processing & shipping: 2-4 weeks
          </Typography>
        </Box>

        {/* Proceed to Payment Button */}
        <Button
          onClick={handleProceedToPayment}
          sx={{
            backgroundColor: '#FF6B35',
            color: '#FFFFFF',
            fontFamily: "'Press Start 2P', cursive",
            fontSize: '0.8rem',
            padding: '12px 24px',
            borderRadius: '4px',
            '&:hover': { backgroundColor: '#E55A2B' },
            textTransform: 'none',
            width: '100%',
            '&:active': {
              backgroundColor: '#D44A20',
              transform: 'scale(0.98)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          🛒 Proceed to Payment - ${getProductPrice()}.00
        </Button>
      </Box>
    </Box>
  );
};

// Dog Products Payment Component
const DogProductsPayment = ({ customization, onClose, onBack }) => {
  const { user } = useAuth();
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [cryptoAmount, setCryptoAmount] = useState('0.0011');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  const cryptoAddresses = {
    BTC: '3KybzUMnSGkJMNmoshXD7wwaeLuENQNpNB',
    ETH: '0xB8ea0cEF886Fa141B3c60E220320FA911d3bBC99',
    DOGE: 'DGRAizMZUuQuqMLSTUt7AxoEsktYryA6s1',
    ZEC: 't1WZrmSP3ZjxxpZqX5zCDpXX6P1UKtgvNHE',
  };

  const orderPrice = customization.price; // Dynamic pricing based on selection

  // Update crypto amount when crypto selection changes
  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        const response = await axios.get('/api/crypto-payment-prices');
        const prices = response.data.prices;

        const cryptoEquivalents = {
          BTC: (orderPrice / prices.BTC).toFixed(8),
          ETH: (orderPrice / prices.ETH).toFixed(8),
          DOGE: (orderPrice / prices.DOGE).toFixed(2),
          ZEC: (orderPrice / prices.ZEC).toFixed(4),
        };

        setCryptoAmount(cryptoEquivalents[selectedCrypto]);
      } catch (error) {
        console.error('Error fetching crypto prices:', error);
        setCryptoAmount('N/A');
      }
    };

    fetchCryptoPrices();
  }, [selectedCrypto, orderPrice]);

  const handleCryptoSelect = (crypto) => {
    setSelectedCrypto(crypto);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(cryptoAddresses[selectedCrypto]);
    alert('Crypto Address Copied!');
  };

  const handleAddressChange = (field, value) => {
    setShippingAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfirmPayment = () => {
    if (!paymentConfirmed) {
      setPaymentConfirmed(true);
    }
  };

  const handlePurchase = async () => {
    if (!confirmChecked) {
      alert('Please confirm your order details before proceeding.');
      return;
    }

    if (!paymentConfirmed) {
      alert('Please confirm that you have sent the crypto payment.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Validate shipping address
    if (!shippingAddress.name || !shippingAddress.address || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zip) {
      alert('Please fill in all shipping address fields.');
      return;
    }

    try {
      await axios.post('/api/shiptee-payment-notification', {
        email,
        cryptoAmount,
        selectedCrypto,
        usdAmount: orderPrice,
        customization,
        shippingAddress,
        productType: 'DogProducts'
      });

      const productNames = [];
      if (customization.selectedProducts.includes('cards')) productNames.push('Card Pack');
      if (customization.selectedProducts.includes('t-shirt')) productNames.push('Custom T-Shirt');
      if (customization.selectedProducts.includes('hat')) productNames.push('Custom Hat');
      if (customization.selectedProducts.includes('socks')) productNames.push('Custom Socks');

      alert(
        `🎉 Order Confirmed!\n\nThank you for your payment!\n\nProducts ordered: ${productNames.join(', ')}\n\nShip to:\n${shippingAddress.name}\n${shippingAddress.address}\n${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}\n\n📧 You'll receive order updates and tracking info via email.\n⏰ Processing & shipping: 2-4 weeks\n\nThank you for choosing Dog T's, Hats n' Cards!`
      );
      onClose();
    } catch (error) {
      console.error('Error processing Dog Products payment:', error);
      alert('Failed to process payment. Please try again.');
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ position: 'relative' }}>
        <RetroCloseButton onClick={onClose} label="Close" sx={{ top: 0, right: 0 }} />

        <RetroTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          '@media (max-width: 600px)': {
            fontSize: '1.2rem',
          },
        }}>
          <LocalShippingIcon />
          Dog T's, Hats n' Cards - Payment
        </RetroTitle>

        {/* Order Summary */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '8px',
            padding: '15px',
            mb: 2,
            border: '1px solid #00BFFF'
          }}
        >
          <Typography sx={{ 
            color: '#FFFFFF', 
            fontSize: '1rem', 
            mb: 2, 
            textAlign: 'center',
            fontFamily: "'Press Start 2P', cursive"
          }}>
            Order Summary
          </Typography>
          
          {/* Product List */}
          {customization.selectedProducts.includes('cards') && (
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              📦 Card Pack - {customization.savedCards?.length || 0} dog cards printed
            </Typography>
          )}
          {customization.selectedProducts.includes('t-shirt') && (
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              👕 Custom T-Shirt{customization.color && customization.size ? ` (${customization.color}, ${customization.size})` : ''}
              {customization.cardData && <span> + {customization.cardData.breedName} Card</span>}
            </Typography>
          )}
          {customization.selectedProducts.includes('hat') && (
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              🧢 Custom Hat{customization.color && customization.size ? ` (${customization.color}, ${customization.size})` : ''}
              {customization.cardData && <span> + {customization.cardData.breedName} Card</span>}
            </Typography>
          )}
          {customization.selectedProducts.includes('socks') && (
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              🧦 Custom Socks{customization.color && customization.size ? ` (${customization.color}, ${customization.size})` : ''}
              {customization.cardData && <span> + {customization.cardData.breedName} Card</span>}
            </Typography>
          )}
          
          {customization.dogName && (
            <Typography sx={{ color: '#FFFFFF', fontSize: '0.8rem', mb: 1 }}>
              ✨ Custom Name: "{customization.dogName}"
            </Typography>
          )}
          
          <Typography sx={{ color: '#50E3C2', fontSize: '1.2rem', fontWeight: 'bold', textAlign: 'center', mt: 2 }}>
            Total: ${orderPrice}.00
          </Typography>
          <Typography sx={{ color: '#FFFFFF', fontSize: '0.7rem', textAlign: 'center', mt: 1 }}>
            ⏰ Processing & shipping: 2-4 weeks
          </Typography>
        </Box>

        {/* Shipping Address Form */}
        <Typography sx={{ color: '#FFFFFF', fontSize: '0.9rem', mb: 1 }}>
          Shipping Address (US Only):
        </Typography>
        
        <TextField
          label="Full Name"
          value={shippingAddress.name}
          onChange={(e) => handleAddressChange('name', e.target.value)}
          fullWidth
          sx={{
            mb: 1,
            backgroundColor: '#283593',
            borderRadius: '5px',
            input: { color: '#FFFFFF' },
            label: { color: '#FFFFFF' },
            '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
          }}
        />

        <TextField
          label="Address"
          value={shippingAddress.address}
          onChange={(e) => handleAddressChange('address', e.target.value)}
          fullWidth
          sx={{
            mb: 1,
            backgroundColor: '#283593',
            borderRadius: '5px',
            input: { color: '#FFFFFF' },
            label: { color: '#FFFFFF' },
            '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
          }}
        />

        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mb: 2,
          '@media (max-width: 600px)': {
            flexDirection: 'column',
            gap: 1,
          },
        }}>
          <TextField
            label="City"
            value={shippingAddress.city}
            onChange={(e) => handleAddressChange('city', e.target.value)}
            sx={{
              flex: 2,
              backgroundColor: '#283593',
              borderRadius: '5px',
              input: { color: '#FFFFFF' },
              label: { color: '#FFFFFF' },
              '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
              '@media (max-width: 600px)': {
                flex: 'none',
              },
            }}
          />
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            '@media (max-width: 600px)': {
              gap: 1,
            },
          }}>
            <TextField
              label="State"
              value={shippingAddress.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              sx={{
                flex: 1,
                backgroundColor: '#283593',
                borderRadius: '5px',
                input: { color: '#FFFFFF' },
                label: { color: '#FFFFFF' },
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
              }}
            />
            <TextField
              label="ZIP"
              value={shippingAddress.zip}
              onChange={(e) => handleAddressChange('zip', e.target.value)}
              sx={{
                flex: 1,
                backgroundColor: '#283593',
                borderRadius: '5px',
                input: { color: '#FFFFFF' },
                label: { color: '#FFFFFF' },
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
              }}
            />
          </Box>
        </Box>

        {/* QR Code Section (reusing existing component) */}
        <QRCodeCanvas
          value={cryptoAddresses[selectedCrypto]}
          size={150}
          style={{ margin: '10px auto', maxWidth: '100%', height: 'auto' }}
        />

        {/* Crypto Address (reusing existing component) */}
        <CopyField
          value={cryptoAddresses[selectedCrypto]}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <ContentCopyIcon
                  onClick={handleCopyToClipboard}
                  sx={{ cursor: 'pointer', color: '#FFFFFF' }}
                />
              </InputAdornment>
            ),
          }}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Crypto Selection (reusing existing logic) */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Bitcoin">
              <Typography
                onClick={() => handleCryptoSelect('BTC')}
                sx={{
                  color: selectedCrypto === 'BTC' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'BTC' ? 'bold' : 'normal',
                }}
              >
                BTC
              </Typography>
            </Tooltip>
            <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
            <Tooltip title="Ethereum">
              <Typography
                onClick={() => handleCryptoSelect('ETH')}
                sx={{
                  color: selectedCrypto === 'ETH' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'ETH' ? 'bold' : 'normal',
                }}
              >
                ETH
              </Typography>
            </Tooltip>
            <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
            <Tooltip title="Dogecoin">
              <Typography
                onClick={() => handleCryptoSelect('DOGE')}
                sx={{
                  color: selectedCrypto === 'DOGE' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'DOGE' ? 'bold' : 'normal',
                }}
              >
                DOGE
              </Typography>
            </Tooltip>
            <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
            <Tooltip title="Zcash">
              <Typography
                onClick={() => handleCryptoSelect('ZEC')}
                sx={{
                  color: selectedCrypto === 'ZEC' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'ZEC' ? 'bold' : 'normal',
                }}
              >
                ZEC
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Payment Summary */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '8px',
            padding: '15px',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.9rem',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Send: {cryptoAmount} {selectedCrypto}
          </Typography>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
          >
            For Dog Products Order (${orderPrice}.00)
          </Typography>
        </Box>

        {/* Crypto Payment Confirmation */}
        <Box sx={{ mb: 2 }}>
          <Button
            onClick={handleConfirmPayment}
            disabled={paymentConfirmed}
            sx={{
              backgroundColor: paymentConfirmed ? '#4CAF50' : '#FF9800',
              color: '#FFFFFF',
              fontFamily: "'Press Start 2P', cursive",
              fontSize: '0.8rem',
              padding: '12px 24px',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: paymentConfirmed ? '#45a049' : '#F57C00',
              },
              textTransform: 'none',
              width: '100%',
              mb: 1,
              '&:disabled': {
                backgroundColor: '#4CAF50',
                color: '#FFFFFF',
              },
            }}
          >
            {paymentConfirmed ? '✅ Payment Confirmed!' : '📧 Confirm Crypto Payment Sent'}
          </Button>
          {paymentConfirmed && (
            <Typography sx={{ color: '#4CAF50', fontSize: '0.7rem', textAlign: 'center' }}>
              Great! We'll verify your payment and process your order.
            </Typography>
          )}
        </Box>

        {/* Email Field (only if not logged in) */}
        {!user?.email && (
          <TextField
            label="Email for Receipt & Tracking"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            sx={{
              backgroundColor: '#283593',
              borderRadius: '5px',
              mb: 2,
              input: { color: '#FFFFFF' },
              label: { color: '#FFFFFF' },
            }}
          />
        )}

        {/* Confirmation */}
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              sx={{
                color: '#00BFFF',
                '&.Mui-checked': { color: '#00BFFF' },
              }}
            />
          }
          label="I confirm my order details and shipping address are correct"
          sx={{
            color: '#FFFFFF',
            fontSize: '0.9rem',
            alignSelf: 'center',
            mb: 2,
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          '@media (max-width: 600px)': {
            flexDirection: 'column',
            gap: 1,
          },
        }}>
          <Button
            onClick={onBack}
            sx={{
              backgroundColor: '#666',
              color: '#FFFFFF',
              fontFamily: "'Press Start 2P', cursive",
              fontSize: '0.8rem',
              padding: '10px 20px',
              borderRadius: '4px',
              '&:hover': { backgroundColor: '#555' },
              textTransform: 'none',
              flex: 1,
              '@media (max-width: 600px)': {
                fontSize: '0.7rem',
                padding: '12px 16px',
                flex: 'none',
              },
            }}
          >
            Back to Design
          </Button>
          
          <Button
            onClick={handlePurchase}
            disabled={!confirmChecked || !paymentConfirmed}
            sx={{
              backgroundColor: (confirmChecked && paymentConfirmed) ? '#4CAF50' : 'rgba(76, 175, 80, 0.3)',
              color: '#FFFFFF',
              fontFamily: "'Press Start 2P', cursive",
              fontSize: '0.8rem',
              padding: '10px 20px',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: (confirmChecked && paymentConfirmed) ? '#45a049' : 'rgba(76, 175, 80, 0.3)',
              },
              textTransform: 'none',
              flex: 2,
              '@media (max-width: 600px)': {
                fontSize: '0.7rem',
                padding: '12px 16px',
                flex: 'none',
              },
            }}
          >
            🎉 Complete Order
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Dashboard Payment Component
const DashboardPaymentPopup = ({ onClose }) => {
  const { user } = useAuth();
  const [selectedPack, setSelectedPack] = useState(10); // Default to middle option
  const [selectedCardCount, setSelectedCardCount] = useState(100);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [cryptoAmount, setCryptoAmount] = useState('0.00015');
  const [usdAmount, setUsdAmount] = useState(`$${selectedPack}.00`);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [email, setEmail] = useState(user?.email || '');

  const cryptoAddresses = {
    BTC: '3KybzUMnSGkJMNmoshXD7wwaeLuENQNpNB',
    ETH: '0xB8ea0cEF886Fa141B3c60E220320FA911d3bBC99',
    DOGE: 'DGRAizMZUuQuqMLSTUt7AxoEsktYryA6s1',
    ZEC: 't1WZrmSP3ZjxxpZqX5zCDpXX6P1UKtgvNHE',
  };

  // Update crypto amount when pack or crypto changes
  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        const response = await axios.get('/api/crypto-payment-prices');
        const prices = response.data.prices;

        const cryptoEquivalents = {
          BTC: (selectedPack / prices.BTC).toFixed(8),
          ETH: (selectedPack / prices.ETH).toFixed(8),
          DOGE: (selectedPack / prices.DOGE).toFixed(2),
          ZEC: (selectedPack / prices.ZEC).toFixed(4),
        };

        setCryptoAmount(cryptoEquivalents[selectedCrypto]);
        setUsdAmount(`$${selectedPack}.00`);
      } catch (error) {
        console.error('Error fetching crypto prices:', error);
        setCryptoAmount('N/A');
      }
    };

    fetchCryptoPrices();
  }, [selectedPack, selectedCrypto]);

  const handlePackSelect = (price, cardCount) => {
    setSelectedPack(price);
    setSelectedCardCount(cardCount);
  };

  const handleCryptoSelect = (crypto) => {
    setSelectedCrypto(crypto);
  };

  const handleConfirmChange = (event) => {
    setConfirmChecked(event.target.checked);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(cryptoAddresses[selectedCrypto]);
    alert('Crypto Address Copied!');
  };

  const handlePurchase = async () => {
    if (!confirmChecked) {
      alert('Please confirm the payment before proceeding.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      await axios.post('/api/payment-notification', {
        email,
        cryptoAmount,
        selectedCrypto,
        usdAmount: selectedPack,
        cardCount: selectedCardCount,
      });

      alert(
        'Receipt sent! Thank you for your payment. You will receive your Dog Cards soon!'
      );
      onClose();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('Failed to process payment. Please try again.');
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ position: 'relative' }}>
        <RetroCloseButton onClick={onClose} label="Close" sx={{ top: 0, right: 0 }} />

        <RetroTitle>Get More Cards!</RetroTitle>

        <Typography
          sx={{
            color: '#FFFFFF',
            fontSize: '0.9rem',
            textAlign: 'center',
            mb: 3,
          }}
        >
          You're running low on Dog Cards! Purchase more to continue discovering
          new breeds.
        </Typography>

        {/* Package Options */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            mb: 3,
          }}
        >
          <CardPackOption
            price={5}
            cardCount={25}
            selectedPack={selectedPack}
            onSelect={handlePackSelect}
          />
          <CardPackOption
            price={10}
            cardCount={100}
            selectedPack={selectedPack}
            onSelect={handlePackSelect}
          />
          <CardPackOption
            price={15}
            cardCount={200}
            selectedPack={selectedPack}
            onSelect={handlePackSelect}
          />
        </Box>

        {/* QR Code Section */}
        <QRCodeCanvas
          value={cryptoAddresses[selectedCrypto]}
          size={150}
          style={{ margin: '10px auto', maxWidth: '100%', height: 'auto' }}
        />

        {/* Crypto Address */}
        <CopyField
          value={cryptoAddresses[selectedCrypto]}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <ContentCopyIcon
                  onClick={handleCopyToClipboard}
                  sx={{ cursor: 'pointer', color: '#FFFFFF' }}
                />
              </InputAdornment>
            ),
          }}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Crypto Selection */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Bitcoin">
              <Typography
                onClick={() => handleCryptoSelect('BTC')}
                sx={{
                  color: selectedCrypto === 'BTC' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'BTC' ? 'bold' : 'normal',
                }}
              >
                BTC
              </Typography>
            </Tooltip>
            <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
            <Tooltip title="Ethereum">
              <Typography
                onClick={() => handleCryptoSelect('ETH')}
                sx={{
                  color: selectedCrypto === 'ETH' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'ETH' ? 'bold' : 'normal',
                }}
              >
                ETH
              </Typography>
            </Tooltip>
            <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
            <Tooltip title="Dogecoin">
              <Typography
                onClick={() => handleCryptoSelect('DOGE')}
                sx={{
                  color: selectedCrypto === 'DOGE' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'DOGE' ? 'bold' : 'normal',
                }}
              >
                DOGE
              </Typography>
            </Tooltip>
            <Typography sx={{ color: '#FFFFFF' }}>|</Typography>
            <Tooltip title="Zcash">
              <Typography
                onClick={() => handleCryptoSelect('ZEC')}
                sx={{
                  color: selectedCrypto === 'ZEC' ? '#FFFFFF' : '#00BFFF',
                  cursor: 'pointer',
                  fontWeight: selectedCrypto === 'ZEC' ? 'bold' : 'normal',
                }}
              >
                ZEC
              </Typography>
            </Tooltip>
          </Box>
        </Box>

        {/* Payment Summary */}
        <Box
          sx={{
            backgroundColor: 'rgba(0, 191, 255, 0.1)',
            borderRadius: '8px',
            padding: '15px',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.9rem',
              textAlign: 'center',
              mb: 1,
            }}
          >
            {cryptoAmount} {selectedCrypto}
          </Typography>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.9rem',
              textAlign: 'center',
            }}
          >
            For {selectedCardCount} Dog Cards (${selectedPack}.00)
          </Typography>
        </Box>

        {/* Confirmation */}
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmChecked}
              onChange={handleConfirmChange}
              sx={{
                color: '#00BFFF',
                '&.Mui-checked': {
                  color: '#00BFFF',
                },
              }}
            />
          }
          label="I confirm this payment"
          sx={{
            color: '#FFFFFF',
            fontSize: '0.9rem',
            alignSelf: 'center',
            mb: 2,
          }}
        />

        {/* Email Field (only if not logged in) */}
        {!user?.email && (
          <TextField
            label="Email for Receipt"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            sx={{
              backgroundColor: '#283593',
              borderRadius: '5px',
              mb: 2,
              input: {
                color: '#FFFFFF',
              },
              label: {
                color: '#FFFFFF',
              },
            }}
          />
        )}

        {/* Purchase Button */}
        <Button
          onClick={handlePurchase}
          disabled={!confirmChecked}
          sx={{
            backgroundColor: confirmChecked
              ? '#00BFFF'
              : 'rgba(0, 191, 255, 0.3)',
            color: '#FFFFFF',
            fontFamily: "'Press Start 2P', cursive",
            fontSize: '0.9rem',
            padding: '10px 20px',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: confirmChecked
                ? '#00A0E0'
                : 'rgba(0, 191, 255, 0.3)',
            },
            textTransform: 'none',
            width: '100%',
          }}
        >
          Purchase with {selectedCrypto}
        </Button>
      </Box>
    </Box>
  );
};

const Dashboard = ({ onClose }) => {
  const { cardGenerationInfo, savedCards, fetchSavedCards, user, recordCardGeneration, saveCard } = useAuth();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [selectedCard, setSelectedCard] = useState(null);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [showShopView, setShowShopView] = useState(false);
  const [showDogProductsCustomizer, setShowDogProductsCustomizer] = useState(false);
  const [showDogProductsPayment, setShowDogProductsPayment] = useState(false);
  const [selectedCardForShipping, setSelectedCardForShipping] = useState(null);
  const [dogProductsCustomization, setDogProductsCustomization] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  // Shared gamertag (one tag across all Satoshi Advance games) editor state
  const [editingTag, setEditingTag] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tagStatus, setTagStatus] = useState('');

  const saveGamerTag = async () => {
    const tag = tagInput.trim();
    if (!tag) {
      setTagStatus('Gamertag cannot be empty');
      return;
    }
    if (tag.length > 20 || !/^[A-Za-z0-9 _]+$/.test(tag)) {
      setTagStatus('1-20 chars: letters, numbers, spaces, _');
      return;
    }
    try {
      const res = await axios.post('/api/users/update-gamertag', {
        email: user.email,
        gamerTag: tag
      });
      setGameStats((prev) => ({ ...(prev || {}), gamerTag: res.data.gamerTag || tag }));
      setEditingTag(false);
      setTagStatus('Saved! It shows across all games.');
      setTimeout(() => setTagStatus(''), 3000);
    } catch (error) {
      setTagStatus(error.response?.data?.error || 'Failed to save');
    }
  };

  // 1) Track which 3-card "page" we're on
  const [visibleIndex, setVisibleIndex] = useState(0);

  // Custom breed generator state — generates a real dog card (same pipeline
  // as the homepage game) and saves it so it can be ordered right away
  const [breedRequestInput, setBreedRequestInput] = useState('');
  const [breedRequestStatus, setBreedRequestStatus] = useState('');
  const [breedRequestSending, setBreedRequestSending] = useState(false);
  const [generatedCard, setGeneratedCard] = useState(null);
  const cardPreviewRef = useRef(null);
  const generatedCardSavedRef = useRef(false);

  const generateBreedCard = async () => {
    const breed = breedRequestInput.trim();
    if (!breed) {
      setBreedRequestStatus('Please type a breed name first');
      return;
    }
    if (!user?.email) {
      setBreedRequestStatus('Please log in to generate cards');
      return;
    }
    if ((cardGenerationInfo?.remainingGenerations || 0) <= 0) {
      setBreedRequestStatus("You've used all 25 cards this month — more on the 1st!");
      return;
    }

    setBreedRequestSending(true);
    setGeneratedCard(null);
    generatedCardSavedRef.current = false;
    setBreedRequestStatus('Generating your card... this can take up to a minute');

    try {
      const recorded = await recordCardGeneration(user.email);
      if (!recorded) {
        throw new Error('Could not record generation');
      }

      const [description, imageUrl] = await Promise.all([
        fetchDogDescription(breed),
        fetchDogImage(breed),
      ]);

      let spriteUrl = null;
      try {
        spriteUrl = await removeBlueBackground(imageUrl);
      } catch (spriteError) {
        spriteUrl = null;
      }

      setGeneratedCard({ breed, description, imageUrl, spriteUrl });
      // Saving happens once the preview image finishes rendering
      // (see handleGeneratedImageLoad)
    } catch (error) {
      console.error('Error generating custom breed card:', error);
      setBreedRequestStatus('Failed to generate card. Please try again.');
      setBreedRequestSending(false);
    }
  };

  // Snapshot the rendered preview card and save it to the account, exactly
  // like downloading a card on the homepage does
  const handleGeneratedImageLoad = async () => {
    if (generatedCardSavedRef.current || !cardPreviewRef.current || !generatedCard) return;
    generatedCardSavedRef.current = true;

    try {
      const canvas = await html2canvas(cardPreviewRef.current, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0,
      });
      const dataURL = canvas.toDataURL('image/png');
      await saveCard(generatedCard.breed, dataURL, generatedCard.spriteUrl);
      if (fetchSavedCards) fetchSavedCards();
      setBreedRequestStatus('Card added to Your Saved Cards — order prints or merch any time!');
      setBreedRequestInput('');
    } catch (error) {
      console.error('Error saving generated card:', error);
      setBreedRequestStatus('Card generated, but saving it failed. Please try again.');
    } finally {
      setBreedRequestSending(false);
    }
  };

  // Read the breed name + description aloud, like the homepage Learn button
  const speakGeneratedCard = () => {
    if (!generatedCard) return;
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(
        `${generatedCard.breed}: ${generatedCard.description}`
      );
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      utterance.pitch = 1.0;
      window.speechSynthesis.speak(utterance);
    } else {
      alert('Speech Synthesis not supported in this browser.');
    }
  };

  // Download the card exactly as previewed
  const downloadGeneratedCard = async () => {
    if (!cardPreviewRef.current || !generatedCard) return;
    try {
      const canvas = await html2canvas(cardPreviewRef.current, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0,
      });
      const dataURL = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataURL;
      link.download = `${generatedCard.breed.replace(/\s+/g, '_')}dogcard.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading generated card:', error);
      setBreedRequestStatus('Download failed. Please try again.');
    }
  };

  // Generate a random trick pose for the same breed (does not count against
  // the monthly limit, matching the homepage Command button)
  const giveGeneratedCardTrick = async () => {
    if (!generatedCard || breedRequestSending) return;
    const action = ACTIONS[Math.floor(Math.random() * ACTIONS.length)];
    setBreedRequestSending(true);
    setBreedRequestStatus(`Generating "${action}" trick...`);
    try {
      const imageUrl = await fetchDogImage(generatedCard.breed, action);
      let spriteUrl = null;
      try {
        spriteUrl = await removeBlueBackground(imageUrl);
      } catch (spriteError) {
        spriteUrl = null;
      }
      // Let the new pose auto-save as its own card once it renders
      generatedCardSavedRef.current = false;
      setGeneratedCard((prev) => ({ ...prev, imageUrl, spriteUrl }));
    } catch (error) {
      console.error('Error generating trick:', error);
      setBreedRequestStatus('Trick generation failed. Please try again.');
      setBreedRequestSending(false);
    }
  };

  // Hand the card to the homepage game and launch find-the-sprite there
  const findGeneratedSprite = () => {
    if (!generatedCard) return;
    try {
      sessionStorage.setItem('customDogCard', JSON.stringify(generatedCard));
    } catch (storageError) {}
    // If the game is already mounted it picks the card up live...
    window.dispatchEvent(new CustomEvent('loadCustomDogCard', { detail: generatedCard }));
    // ...and the homepage turns on game mode, closes this dashboard, and scrolls
    window.dispatchEvent(new CustomEvent('openDogGame'));
  };

  useEffect(() => {
    if (fetchSavedCards) {
      fetchSavedCards();
    }
  }, [fetchSavedCards]);

  // Fetch game stats for logged-in users; refetch when the tab regains
  // focus so a gamertag set on a game app shows up when returning here.
  useEffect(() => {
    const fetchGameStats = async () => {
      if (user?.email) {
        try {
          const response = await axios.get(`/api/users/stats/${encodeURIComponent(user.email)}`);
          setGameStats(response.data);
        } catch (error) {
          console.error('Error fetching game stats:', error);
        }
      }
    };

    fetchGameStats();

    const onVisible = () => {
      if (!document.hidden) fetchGameStats();
    };
    window.addEventListener('focus', fetchGameStats);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', fetchGameStats);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  // Calculate generated cards (assuming total is 25)
  const totalCards = 25;
  const remaining = cardGenerationInfo?.remainingGenerations || 0;
  const generated = totalCards - remaining;

  // Check if user is running low on cards (5 or fewer)
  useEffect(() => {
    // For testing, you can set this to true to always show the popup
    // For production, use: if (remaining <= 5 && remaining > 0)
    if (remaining <= 5 && remaining > 0) {
      setShowPaymentPopup(true);
    }
  }, [remaining]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Format date and time
  const formattedDate = currentDateTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const formattedTime = currentDateTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Handlers
  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  const handleCloseDetailView = (e) => {
    e.stopPropagation();
    setSelectedCard(null);
  };

  // Handle payment popup close
  const handlePaymentClose = () => {
    setShowPaymentPopup(false);
  };

  // Handle Dog Products functionality
  const handleDogProductsClick = (card) => {
    setSelectedCardForShipping(card);
    setShowDogProductsCustomizer(true);
  };

  // Handle Card Pack Only (from dashboard directly)
  const handleCardPackOnly = () => {
    setSelectedCardForShipping(null); // No specific card needed for card pack
    setShowDogProductsCustomizer(true);
  };

  const handleDogProductsCustomizerClose = (customization) => {
    if (customization) {
      // User proceeded to payment - store customization and show payment
      setDogProductsCustomization(customization);
      setShowDogProductsCustomizer(false);
      setShowDogProductsPayment(true);
    } else {
      // User cancelled - close everything
      setShowDogProductsCustomizer(false);
      setSelectedCardForShipping(null);
    }
  };

  const handleDogProductsPaymentClose = () => {
    setShowDogProductsPayment(false);
    setShowDogProductsCustomizer(false);
    setSelectedCardForShipping(null);
    setDogProductsCustomization(null);
  };

  const handleDogProductsPaymentBack = () => {
    setShowDogProductsPayment(false);
    setShowDogProductsCustomizer(true);
  };

  // 2) Show next 3
  const showNextCards = () => {
    if (visibleIndex + 3 < savedCards.length) {
      setVisibleIndex((prev) => prev + 3);
    }
  };

  // 3) Show previous 3
  const showPreviousCards = () => {
    if (visibleIndex - 3 >= 0) {
      setVisibleIndex((prev) => prev - 3);
    }
  };

  if (selectedCard) {
    // Show detail view for selected card
    return (
      <Overlay onClick={onClose}>
        <PopupContainer onClick={(e) => e.stopPropagation()}>
          <Box sx={{ position: 'relative' }}>
            <RetroCloseButton onClick={handleCloseDetailView} label="Close card view" sx={{ top: 0, right: 0 }} />

            {/* Just show the image directly, no additional elements */}
            <Box
              component="img"
              src={selectedCard.imageUrl}
              alt={selectedCard.breedName}
              sx={{
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                maxHeight: '70vh',
                borderRadius: '8px',
                mt: 4,
                mb: 2,
              }}
            />
          </Box>
        </PopupContainer>
      </Overlay>
    );
  }

  // Main dashboard view
  return (
    <Overlay onClick={onClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        {showSubscribePopup ? (
          // Closing subscribe dismisses the whole console back to the page
          <PaymentPopup embedded onClose={onClose} />
        ) : showPaymentPopup ? (
          <DashboardPaymentPopup onClose={handlePaymentClose} />
        ) : showDogProductsPayment ? (
          <DogProductsPayment
            customization={dogProductsCustomization}
            onClose={handleDogProductsPaymentClose}
            onBack={handleDogProductsPaymentBack}
          />
        ) : showDogProductsCustomizer ? (
          <DogProductsCustomizer 
            selectedCard={selectedCardForShipping}
            savedCards={savedCards}
            onClose={handleDogProductsCustomizerClose} 
          />
        ) : showShopView ? (
          // Shop rendered inside the same console card, like Subscribe —
          // ordering hands off to the customizer/payment views above
          <>
            <RetroCloseButton onClick={onClose} label="Close Shop" />

            <RetroTitle>Shop</RetroTitle>

            <Typography
              sx={{
                color: '#FFFFFF',
                fontSize: '0.85rem',
                textAlign: 'center',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              Custom products featuring your favorite dog breeds. Professional
              printing, free US shipping.
            </Typography>

            {SHOP_PRODUCTS.map((product) => (
              <Box
                key={product.id}
                sx={{
                  position: 'relative',
                  background:
                    'linear-gradient(145deg, rgba(0, 191, 255, 0.1) 0%, rgba(0, 191, 255, 0.05) 100%)',
                  border: '2px solid rgba(0, 191, 255, 0.3)',
                  borderRadius: '12px',
                  padding: '14px 16px',
                  marginBottom: '14px',
                  textAlign: 'left',
                }}
              >
                {product.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: '#FF6B35',
                      color: '#FFFFFF',
                      fontSize: '0.6rem',
                      fontWeight: 'bold',
                      borderRadius: '10px',
                      padding: '3px 8px',
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    mb: 0.5,
                    pr: product.popular ? '90px' : 0,
                  }}
                >
                  {product.name}
                </Typography>
                <Typography
                  sx={{
                    color: '#50E3C2',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    fontFamily: "'Press Start 2P', cursive",
                    mb: 1,
                  }}
                >
                  {product.price}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  {product.description}
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  {product.features.map((feature, index) => (
                    <Typography
                      key={index}
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.72rem',
                        mb: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ marginRight: '8px', color: '#50E3C2' }}>•</span>
                      {feature}
                    </Typography>
                  ))}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCardPackOnly}
                  sx={{
                    width: '100%',
                    background:
                      'linear-gradient(45deg, #FF6B35 30%, #F7931E 90%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontFamily: "'Press Start 2P', cursive",
                    fontSize: '0.65rem',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease',
                    '&:hover': { transform: 'translateY(-2px)' },
                  }}
                >
                  Customize &amp; Order
                </Box>
              </Box>
            ))}

            <Box
              sx={{
                backgroundColor: 'rgba(0, 191, 255, 0.1)',
                borderRadius: '10px',
                padding: '12px 14px',
                textAlign: 'center',
                border: '1px solid rgba(0, 191, 255, 0.3)',
                mb: 1,
              }}
            >
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '0.72rem',
                  lineHeight: 1.7,
                }}
              >
                2-4 weeks custom printing • Free US shipping
                <br />
                Premium materials • Email tracking updates
              </Typography>
            </Box>

            {/* Same plain word links as the main dashboard view */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                mt: 2,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setShowShopView(false)}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#00BFFF' },
                }}
              >
                Dashboard
              </Box>
              <Box
                component="span"
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '11px',
                  fontWeight: 'bold',
                }}
              >
                /
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setShowShopView(false);
                  setShowSubscribePopup(true);
                }}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#00BFFF' },
                }}
              >
                Subscribe
              </Box>
            </Box>
          </>
        ) : (
          <>
            <RetroCloseButton onClick={onClose} label="Close Dashboard" />

            <RetroTitle>Dashboard</RetroTitle>

            {/* User Account Info */}
            {user?.email && (
              <Box
                sx={{
                  backgroundColor: 'rgba(80, 227, 194, 0.15)',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid rgba(80, 227, 194, 0.3)',
                  textAlign: 'center'
                }}
              >
                <Typography
                  sx={{
                    color: '#50E3C2',
                    fontSize: '0.8rem',
                    fontFamily: "'Press Start 2P', cursive",
                    marginBottom: '8px'
                  }}
                >
                  Welcome back!
                </Typography>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                >
                  {user.email}
                </Typography>

                {/* Shared gamertag — the same tag shows on the wheel, flip,
                    and tanks scoreboards/leaderboards */}
                <Typography
                  sx={{
                    color: '#00BFFF',
                    fontSize: '0.8rem',
                    fontFamily: "'Press Start 2P', cursive",
                    marginTop: '12px'
                  }}
                >
                  🎮 {gameStats?.gamerTag || 'No gamertag yet'}
                </Typography>

                {editingTag ? (
                  <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="YourGamertag"
                      maxLength={20}
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '2px solid #00BFFF',
                        background: '#283593',
                        color: '#FFFFFF',
                        fontSize: '0.9rem',
                        width: '180px'
                      }}
                    />
                    <button
                      onClick={saveGamerTag}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: '#10B981',
                        color: '#FFFFFF',
                        fontFamily: "'Press Start 2P', cursive",
                        fontSize: '0.6rem',
                        cursor: 'pointer'
                      }}
                    >
                      SAVE
                    </button>
                    <button
                      onClick={() => { setEditingTag(false); setTagStatus(''); }}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #FF6B6B',
                        background: 'none',
                        color: '#FF6B6B',
                        fontSize: '0.7rem',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </Box>
                ) : (
                  <button
                    onClick={() => { setEditingTag(true); setTagInput(gameStats?.gamerTag || ''); setTagStatus(''); }}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      background: 'none',
                      color: '#00BFFF',
                      fontFamily: "'Press Start 2P', cursive",
                      fontSize: '0.55rem',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {gameStats?.gamerTag ? 'EDIT GAMERTAG' : 'CREATE GAMERTAG'}
                  </button>
                )}

                {tagStatus && (
                  <Typography
                    sx={{
                      color: tagStatus.startsWith('Saved') ? '#22c55e' : '#FF6B6B',
                      fontSize: '0.7rem',
                      marginTop: '6px'
                    }}
                  >
                    {tagStatus}
                  </Typography>
                )}
              </Box>
            )}

            <StatCard>
              <StatLabel sx={{ fontSize: '0.7rem' }}>Dog Cards (This Month)</StatLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                  <StatValue sx={{ fontSize: '1rem', mr: 1 }}>
                    {generated}
                  </StatValue>
                  <Typography
                    sx={{
                      color: '#FFFFFF',
                      fontFamily: "'Press Start 2P', cursive",
                      fontSize: '0.8rem',
                    }}
                  >
                    Generated
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <StatValue sx={{ fontSize: '1rem' }}>
                    {remaining}
                  </StatValue>
                  <Typography
                    sx={{
                      color: '#FFFFFF',
                      fontFamily: "'Press Start 2P', cursive",
                      fontSize: '0.8rem',
                      ml: 1,
                    }}
                  >
                    Remaining
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.65rem',
                    mt: 1,
                  }}
                >
                  Your 25 free generations reset on the 1st of each month
                </Typography>
              </Box>
            </StatCard>

            {/* Card Pack Button */}
            {savedCards && savedCards.length > 0 && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  onClick={handleCardPackOnly}
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    fontFamily: "'Press Start 2P', cursive",
                    fontSize: '0.68rem',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#45a049',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      backgroundColor: '#3d8b40',
                      transform: 'scale(0.98)',
                    },
                    textTransform: 'none',
                    mb: 2,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  📦 Print All My Cards ({savedCards.length} cards) - $50
                </Button>
              </Box>
            )}

            {/* Saved Cards Section */}
            <Box sx={{ mt: 4, width: '100%' }}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '1.2rem',
                  mb: 2,
                  textShadow: '0 0 5px rgba(0, 191, 255, 0.5)',
                }}
              >
                Your Saved Cards
              </Typography>

              {!savedCards || savedCards.length === 0 ? (
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: "'Press Start 2P', cursive",
                    fontSize: '0.8rem',
                  }}
                >
                  No saved cards yet
                </Typography>
              ) : (
                <>
                  {/* 4) Show exactly 3 cards at a time */}
                  <Box
  sx={{
    display: 'grid',
    // Use minmax to ensure each column is at least 120px but shrinks if needed
    gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))',
    gap: 2,
    mb: 2,
  }}
>
  {savedCards
    .slice(visibleIndex, visibleIndex + 3)
    .map((card) => (
      <Box
        key={card.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          component="img"
          src={card.imageUrl}
          alt={card.breedName}
          onClick={() => handleCardClick(card)}
          sx={{
            width: '120px',
            height: '120px',
            objectFit: 'cover',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 0 8px rgba(0, 191, 255, 0.5)',
            },
          }}
        />
        
        {/* DOG PRODUCTS Button */}
        <Button
          onClick={() => handleDogProductsClick(card)}
          sx={{
            backgroundColor: '#FF6B35',
            color: '#FFFFFF',
            fontFamily: "'Press Start 2P', cursive",
            fontSize: '0.5rem',
            padding: '4px 8px',
            borderRadius: '4px',
            minHeight: 'unset',
            '&:hover': {
              backgroundColor: '#E55A2B',
            },
            '&:active': {
              backgroundColor: '#D44A20',
              transform: 'scale(0.98)',
            },
            textTransform: 'none',
            width: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            transition: 'all 0.2s ease',
            '@media (max-width: 600px)': {
              fontSize: '0.45rem',
              padding: '6px 8px',
              minHeight: '32px',
              '&:active': {
                transform: 'scale(0.95)',
              },
            },
          }}
        >
          <LocalShippingIcon sx={{ 
            fontSize: '10px',
            '@media (max-width: 600px)': {
              fontSize: '8px',
            },
          }} />
          T's, HATS & CARDS
        </Button>
      </Box>
    ))}
</Box>


                  {/* 5) Arrow up/down buttons to navigate */}
                  <Box
                    sx={{
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <IconButton
                      onClick={showPreviousCards}
                      disabled={visibleIndex === 0}
                      sx={{
                        color: '#FFFFFF',
                        '&.Mui-disabled': {
                          opacity: 0.3,
                        },
                      }}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>

                    <IconButton
                      onClick={showNextCards}
                      disabled={visibleIndex + 3 >= savedCards.length}
                      sx={{
                        color: '#FFFFFF',
                        '&.Mui-disabled': {
                          opacity: 0.3,
                        },
                      }}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </Box>
                </>
              )}
            </Box>

            {/* Request Custom Breed Section */}
            <Box sx={{ mt: 4, width: '100%' }}>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '1.2rem',
                  mb: 1,
                  textShadow: '0 0 5px rgba(0, 191, 255, 0.5)',
                }}
              >
                Request Custom Breed
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.8rem',
                  mb: 2,
                }}
              >
                Want a specific dog? Start typing and pick a breed (or type any
                breed) and we'll generate its card right here. It saves to Your
                Saved Cards so you can order prints and merch right away.
              </Typography>
              <Autocomplete
                freeSolo
                options={DOG_BREEDS}
                inputValue={breedRequestInput}
                onInputChange={(event, value) => setBreedRequestInput(value)}
                // Only suggest once the user starts typing, and only breeds
                // beginning with what they've typed, so the list stays short
                filterOptions={(options, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return [];
                  return options.filter((o) => o.toLowerCase().startsWith(q));
                }}
                slotProps={{
                  popper: { sx: { zIndex: 10001 } },
                  paper: {
                    sx: {
                      backgroundColor: '#283593',
                      color: '#FFFFFF',
                      '& .MuiAutocomplete-option': { fontSize: '0.9rem' },
                      '& .MuiAutocomplete-noOptions': { color: 'rgba(255,255,255,0.7)' },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type a breed name"
                    sx={{
                      backgroundColor: '#283593',
                      borderRadius: '5px',
                      input: { color: '#FFFFFF' },
                      label: { color: '#FFFFFF' },
                      '.MuiOutlinedInput-notchedOutline': { borderColor: '#00BFFF' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFFFFF' },
                    }}
                  />
                )}
              />
              <Button
                onClick={generateBreedCard}
                disabled={breedRequestSending}
                sx={{
                  mt: 2,
                  backgroundColor: '#4CAF50',
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '0.7rem',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#45a049' },
                  '&.Mui-disabled': { backgroundColor: 'rgba(76, 175, 80, 0.4)', color: '#DDDDDD' },
                }}
              >
                {breedRequestSending ? 'Generating...' : 'Generate Card'}
              </Button>
              {breedRequestStatus && (
                <Typography
                  sx={{
                    color: breedRequestStatus.startsWith('Card added') || breedRequestStatus.startsWith('Generating')
                      ? '#50E3C2'
                      : '#FF6B6B',
                    fontSize: '0.75rem',
                    mt: 1,
                  }}
                >
                  {breedRequestStatus}
                </Typography>
              )}

              {/* Retro three-dot loading animation, same as the homepage game */}
              {breedRequestSending && (
                <>
                  <style>{`
                    @keyframes retroDot1 { 0%, 9% { opacity: 0; } 10%, 39% { opacity: 1; } 40%, 100% { opacity: 0; } }
                    @keyframes retroDot2 { 0%, 19% { opacity: 0; } 20%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
                    @keyframes retroDot3 { 0%, 29% { opacity: 0; } 30%, 59% { opacity: 1; } 60%, 100% { opacity: 0; } }
                    .retro-loading-dots { display: flex; gap: 4px; justify-content: center; align-items: center; }
                    .retro-dot { width: 8px; height: 8px; background-color: #FFFFFF; border-radius: 0; opacity: 0; animation-duration: 1.8s; animation-iteration-count: infinite; animation-timing-function: steps(1, end); }
                    .retro-dot:nth-child(1) { animation-name: retroDot1; }
                    .retro-dot:nth-child(2) { animation-name: retroDot2; }
                    .retro-dot:nth-child(3) { animation-name: retroDot3; }
                  `}</style>
                  <Box className="retro-loading-dots" sx={{ mt: 1.5 }}>
                    <Box className="retro-dot" />
                    <Box className="retro-dot" />
                    <Box className="retro-dot" />
                  </Box>
                </>
              )}

              {/* Generated card preview — snapshotted and saved on load */}
              {generatedCard && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Box
                    ref={cardPreviewRef}
                    sx={{
                      width: '230px',
                      backgroundColor: '#1a237e',
                      border: '3px solid #00BFFF',
                      borderRadius: '10px',
                      padding: '12px',
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontFamily: "'Press Start 2P', cursive",
                        fontSize: '0.6rem',
                        mb: 1,
                        lineHeight: 1.6,
                      }}
                    >
                      {generatedCard.breed}
                    </Typography>
                    <Box
                      component="img"
                      src={generatedCard.imageUrl}
                      alt={generatedCard.breed}
                      crossOrigin="anonymous"
                      onLoad={handleGeneratedImageLoad}
                      sx={{ width: '100%', borderRadius: '6px', display: 'block' }}
                    />
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.6rem',
                        mt: 1,
                        lineHeight: 1.5,
                      }}
                    >
                      {generatedCard.description}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Card actions — same features as the homepage game */}
              {generatedCard && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {[
                    { label: 'Search', tip: 'Find the sprite! Launches the search game with this dog', onClick: findGeneratedSprite },
                    { label: 'Listen', tip: 'Hear the breed description read aloud', onClick: speakGeneratedCard },
                    { label: 'Download', tip: 'Download this card as an image', onClick: downloadGeneratedCard },
                    { label: 'Command', tip: 'Give the dog a trick to do (saves as a new card)', onClick: giveGeneratedCardTrick },
                  ].map((action) => (
                    <Tooltip key={action.label} title={action.tip} arrow>
                      <Button
                        onClick={action.onClick}
                        disabled={breedRequestSending}
                        sx={{
                          backgroundColor: '#283593',
                          color: '#FFFFFF',
                          border: '2px solid #00BFFF',
                          fontFamily: "'Press Start 2P', cursive",
                          fontSize: '0.5rem',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          minWidth: 'unset',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#3949ab', borderColor: '#FFFFFF' },
                          '&.Mui-disabled': { opacity: 0.4, color: '#FFFFFF' },
                        }}
                      >
                        {action.label}
                      </Button>
                    </Tooltip>
                  ))}
                </Box>
              )}
            </Box>

            {/* Mini Games Stats Section - Only for logged-in users */}
            {user?.email && gameStats && (
              <Box sx={{ mt: 2, width: '100%' }}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: "'Press Start 2P', cursive",
                    fontSize: '0.85rem',
                    mb: 1.5,
                    textAlign: 'center',
                    textShadow: '0 0 5px rgba(0, 191, 255, 0.5)',
                  }}
                >
                  Mini Games Stats
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 2,
                    '@media (max-width: 600px)': {
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 1.5,
                    },
                  }}
                >
                  {/* Tanks */}
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0, 191, 255, 0.15)',
                      borderRadius: '8px',
                      padding: '10px 8px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '@media (max-width: 600px)': {
                        padding: '8px 6px',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#50E3C2',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        fontFamily: "'Press Start 2P', cursive",
                        mb: 0.5,
                        '@media (max-width: 600px)': {
                          fontSize: '0.85rem',
                        },
                      }}
                    >
                      {gameStats.bitTanks?.gamesPlayed || 0} / {gameStats.bitTanks?.totalGamesAllowed || 3}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: "'Press Start 2P', cursive",
                        '@media (max-width: 600px)': {
                          fontSize: '0.6rem',
                        },
                      }}
                    >
                      Tanks
                    </Typography>
                  </Box>

                  {/* Wheel */}
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0, 191, 255, 0.15)',
                      borderRadius: '8px',
                      padding: '10px 8px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '@media (max-width: 600px)': {
                        padding: '8px 6px',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#50E3C2',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        fontFamily: "'Press Start 2P', cursive",
                        mb: 0.5,
                        '@media (max-width: 600px)': {
                          fontSize: '0.85rem',
                        },
                      }}
                    >
                      {gameStats.bitWheel?.totalSpins || 0} / {(gameStats.bitWheel?.totalSpins || 0) + (gameStats.bitWheel?.spinsRemaining || 10)}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: "'Press Start 2P', cursive",
                        '@media (max-width: 600px)': {
                          fontSize: '0.6rem',
                        },
                      }}
                    >
                      Wheel
                    </Typography>
                  </Box>

                  {/* Flip */}
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0, 191, 255, 0.15)',
                      borderRadius: '8px',
                      padding: '10px 8px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '@media (max-width: 600px)': {
                        padding: '8px 6px',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#50E3C2',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        fontFamily: "'Press Start 2P', cursive",
                        mb: 0.5,
                        '@media (max-width: 600px)': {
                          fontSize: '0.85rem',
                        },
                      }}
                    >
                      {gameStats.bitFlip?.totalFlips || 0} / {(gameStats.bitFlip?.totalFlips || 0) + (gameStats.bitFlip?.flipsRemaining || 25)}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: "'Press Start 2P', cursive",
                        '@media (max-width: 600px)': {
                          fontSize: '0.6rem',
                        },
                      }}
                    >
                      Flip
                    </Typography>
                  </Box>

                  {/* Dog Sprite */}
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0, 191, 255, 0.15)',
                      borderRadius: '8px',
                      padding: '10px 8px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '@media (max-width: 600px)': {
                        padding: '8px 6px',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#50E3C2',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        fontFamily: "'Press Start 2P', cursive",
                        mb: 0.5,
                        '@media (max-width: 600px)': {
                          fontSize: '0.85rem',
                        },
                      }}
                    >
                      {gameStats.bitDogs?.gamesPlayed || 0} / {(gameStats.bitDogs?.gamesPlayed || 0) + (gameStats.bitDogs?.gamesRemaining || 5)}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: "'Press Start 2P', cursive",
                        '@media (max-width: 600px)': {
                          fontSize: '0.6rem',
                        },
                      }}
                    >
                      Dog Sprite
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Referral Section - Only for logged-in users */}
            {user?.email && gameStats && (
              <Box sx={{ mt: 2, width: '100%' }}>
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: "'Press Start 2P', cursive",
                    fontSize: '0.85rem',
                    mb: 1.5,
                    textAlign: 'center',
                    textShadow: '0 0 5px rgba(0, 191, 255, 0.5)',
                  }}
                >
                  Refer Friends & Earn Free Games
                </Typography>

                <Box
                  sx={{
                    backgroundColor: 'rgba(80, 227, 194, 0.15)',
                    borderRadius: '8px',
                    padding: '14px',
                    border: '1px solid rgba(80, 227, 194, 0.3)',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#50E3C2',
                      fontSize: '0.9rem',
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    Get 25 Free Games (5 per mini game) for every friend who signs up and verifies their email!
                  </Typography>

                  {/* Referral Code Display */}
                  <Box
                    sx={{
                      backgroundColor: 'rgba(0, 191, 255, 0.15)',
                      borderRadius: '8px',
                      padding: '15px',
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.8rem',
                        mb: 1,
                      }}
                    >
                      Your Referral Code:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          color: '#50E3C2',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          fontFamily: "'Press Start 2P', cursive",
                          letterSpacing: '1px',
                          wordBreak: 'break-all',
                          '@media (max-width: 600px)': {
                            fontSize: '0.72rem',
                          },
                        }}
                      >
                        {gameStats.referralCode || 'Loading...'}
                      </Typography>
                      <Tooltip title="Copy to clipboard">
                        <IconButton
                          onClick={() => {
                            navigator.clipboard.writeText(gameStats.referralCode);
                            alert('Referral code copied to clipboard!');
                          }}
                          sx={{
                            color: '#00BFFF',
                            '&:hover': {
                              color: '#FFFFFF',
                            },
                          }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Referral Stats */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 2,
                      '@media (max-width: 600px)': {
                        gridTemplateColumns: '1fr',
                        gap: 1.5,
                      },
                    }}
                  >
                    {/* Total Referrals */}
                    <Box
                      sx={{
                        backgroundColor: 'rgba(0, 191, 255, 0.15)',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#50E3C2',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          fontFamily: "'Press Start 2P', cursive",
                          mb: 1,
                        }}
                      >
                        {gameStats.referralCount || 0}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          fontFamily: "'Press Start 2P', cursive",
                        }}
                      >
                        Friends Referred
                      </Typography>
                    </Box>

                    {/* Total Games Earned */}
                    <Box
                      sx={{
                        backgroundColor: 'rgba(0, 191, 255, 0.15)',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#50E3C2',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          fontFamily: "'Press Start 2P', cursive",
                          mb: 1,
                        }}
                      >
                        {gameStats.referralEarnings || 0}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#FFFFFF',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          fontFamily: "'Press Start 2P', cursive",
                        }}
                      >
                        Games Earned
                      </Typography>
                    </Box>
                  </Box>

                  {/* How to Share */}
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        color: '#FFFFFF',
                        fontSize: '0.8rem',
                        mb: 1,
                        textAlign: 'center',
                      }}
                    >
                      Share your referral code when your friends sign up on any mini game!
                    </Typography>
                    <Typography
                      sx={{
                        color: '#00BFFF',
                        fontSize: '0.7rem',
                        textAlign: 'center',
                        fontStyle: 'italic',
                      }}
                    >
                      Works on Tanks, Flip, Wheel, and Dog Sprite
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <DateTimeDisplay>
              {formattedDate} • {formattedTime}
            </DateTimeDisplay>

            {/* Plain word links, matching the game dashboards' bottom navs */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', mt: 2 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowShopView(true)}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#00BFFF' },
                }}
              >
                Shop
              </Box>
              <Box
                component="span"
                sx={{
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '11px',
                  fontWeight: 'bold',
                }}
              >
                /
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setShowSubscribePopup(true)}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: '#FFFFFF',
                  fontFamily: "'Press Start 2P', cursive",
                  fontSize: '11px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#00BFFF' },
                }}
              >
                Subscribe
              </Box>
            </Box>


            {/* Bottom close: reachable without scrolling back to the top X */}
            <Button
              onClick={onClose}
              fullWidth
              sx={{
                mt: 2,
                backgroundColor: '#00BFFF',
                color: '#FFFFFF',
                fontFamily: "'Press Start 2P', cursive",
                fontSize: '0.8rem',
                padding: '12px',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#0095ff' },
              }}
            >
              CLOSE
            </Button>
          </>
        )}
      </PopupContainer>
    </Overlay>
  );
};

export default Dashboard;
